import { useEffect, useState } from 'react'
import { createContainer } from 'unstated-next'
import { OpenAPI } from 'openapi-types'
import { validateSpec } from '@waylay/oas-generator'
import clientContainer from '~/lib/client'
import { isEmpty } from 'lodash-es'
import { ContentType } from '@waylay/oas-generator/dist/lib/types'
import { Plug } from '@waylay/client/dist/plugs'

export enum ImportType {
  File,
  Url,
}

interface IGeneratedPlug {
  name?: string
  path?: string
  operation?: string
  server?: string
  security?: string
  flow?: string
}

const EMPTY_STRING = ''
const SUPPORTED_SPEC_FILE_EXTENSIONS = {
  '.json': ContentType.JSON,
  '.yaml': ContentType.FULL_YAML,
  '.yml': ContentType.SHORT_YAML,
}

const useUploadOpenApiPlug = () => {
  const [importType, setImportType] = useState<ImportType>(ImportType.File)
  const [url, setUrl] = useState<string>(EMPTY_STRING)
  const [file, setFile] = useState<string>(EMPTY_STRING)
  const [fileName, setFileName] = useState<string>(EMPTY_STRING)
  const [validationError, setValidationError] = useState<string>(null)
  const [api, setApi] = useState<OpenAPI.Document>(null)
  const [plug, setPlug] = useState<IGeneratedPlug>({})
  const [isPlugNameAllowed, setIsPlugNameAllowed] = useState<boolean>(true)
  const [generatePlugError, setGeneratePlugError] = useState<string>('')
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [isInCreation, setIsInCreation] = useState<boolean>(false)

  useEffect(() => {
    if (!isEmpty(api) && !isDirty) {
      setIsDirty(true)
    }
  }, [file])

  useEffect(() => {
    if (!isEmpty(api) && !isDirty) {
      setIsDirty(true)
    }
  }, [url])

  useEffect(() => {
    if (generatePlugError) {
      setGeneratePlugError('')
    }
  }, [plug])

  const getContentType = (importType: ImportType, fileName: string) => {
    if (importType !== ImportType.File) {
      return ContentType.UNKNOWN
    }

    const extension = Object.keys(SUPPORTED_SPEC_FILE_EXTENSIONS).find(ext =>
      fileName.includes(ext),
    )

    return extension
      ? SUPPORTED_SPEC_FILE_EXTENSIONS[extension]
      : ContentType.UNKNOWN
  }

  const validateApi = async (spec: string) => {
    try {
      const fileType = getContentType(importType, fileName)
      const api = await validateSpec(spec, fileType)

      setApi(api)
      setValidationError('')
      setIsDirty(false)
    } catch (err) {
      throw new Error(err)
    }
  }

  const setPlugNameAvailability = async (plugName: string) => {
    const plug = (await clientContainer.client.sensors.list({
      name: plugName,
      status: 'any',
    })) as Plug[]
    setIsPlugNameAllowed(plug.length === 0)
  }

  useEffect(() => {
    setPlug({})
  }, [api])

  useEffect(() => {
    setValidationError(null)
    if (importType === ImportType.Url) {
      setFile('')
    } else {
      setUrl('')
    }
    setApi(null)
    setFileName('')
    setGeneratePlugError('')
  }, [importType])

  return {
    url,
    setUrl,
    importType,
    setImportType,
    file,
    setFile,
    api,
    setApi,
    fileName,
    setFileName,
    validationError,
    setValidationError,
    plug,
    setPlug,
    validateApi,
    setPlugNameAvailability,
    isPlugNameAllowed,
    generatePlugError,
    setGeneratePlugError,
    isDirty,
    setIsInCreation,
    isInCreation,
  }
}

export const OpenApiContainer = createContainer(useUploadOpenApiPlug)
